import {TranslateService} from '@ngx-translate/core';
import {Component, Inject, OnDestroy, OnInit, Optional} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {
    CallInvoicesDTO,
    BookingStatusDTO,
    CallInvoiceIncident,
    CallInvoicePartnerConfigurationDto,
    CallInvoiceScheduleSendRequest,
    ConfiguratorCallInvoicesService,
    ConfiguratorSaveQueryService,
    ConfiguratorSendInBlueService,
    EngineSearchInvoiceService,
    GenerateData,
    GetSmtpTemplateOverviewDTO,
    SaveQueryToken,
    SearchInvoicePrepareDataDTO,
    SearchInvoicePrepareDataVO,
    InvoicePeriodPreview
} from '@tj/tj-dmp-lib-api';
import {MenuItem, MessageService, SelectItem} from 'primeng/api';
import {UtilsService} from '../../../../services/utils.service';
import {ActivatedRoute, Router} from '@angular/router';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {fromEvent, merge, Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, filter, finalize, startWith, switchMap, take, takeUntil} from 'rxjs/operators';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {AccountService} from '../../../../auth/account.service';
import {CALL_INVOICE_FIELDS} from './call-invoice-fields';
import {invoiceCommandStatusItems, InvoiceCommandStatusItem} from './invoice-command-statuses';

type SearchInvoicePersonalization = {
    name?: string | null;
    description?: string | null;
    billingDirection?: 'TENANT_TO_PROVIDER' | 'TENANT_TO_PARTNER' | null;
    partnerCodes?: string[] | null;
    statusCommandsByProduct?: Record<string, string[]> | null;
    invoiceTitle?: string | null;
    statementSuffix?: string | null;
    amountMode?: string | null;
    billingFrequency?: string | null;
    invoiceGenerationDayOfMonth?: number | null;
    automaticGenerationEnabled?: boolean | null;
};

type CallInvoiceGenerationFileView = {
    id?: string | null;
    productType?: string | null;
    fileName?: string | null;
    filePath?: string | null;
    contentType?: string | null;
    splitActions?: MenuItem[];
};

type CallInvoiceGenerationAffiliationView = {
    affiliation?: string | null;
    clientName?: string | null;
    status?: string | null;
    errorMsg?: string | null;
    invoiceMail?: {
        email?: string | null;
        recipients?: Array<{email?: string | null; name?: string | null}>;
        clientName?: string | null;
        [key: string]: unknown;
    } | null;
    pdfFile?: CallInvoiceGenerationFileView | null;
    excelFiles?: CallInvoiceGenerationFileView[] | null;
};

type CallInvoiceGenerationView = {
    rowKey?: string | null;
    id?: string | null;
    billingFrequency?: string | null;
    rangeDateStart?: string | Date | number[] | null;
    rangeDateEnd?: string | Date | number[] | null;
    status?: string | null;
    errorMsg?: string | null;
    generatedAt?: string | Date | number[] | null;
    validatedAt?: string | Date | number[] | null;
    sendedAt?: string | Date | number[] | null;
    sentAt?: string | Date | number[] | null;
    plannedSendDate?: string | Date | number[] | null;
    storageFolder?: string | null;
    affiliations?: string[] | null;
    affiliationDetails?: CallInvoiceGenerationAffiliationView[] | null;
    sendSplitActions?: MenuItem[];
};

type CurrentPreviewData = {
    partner: string;
    generationId?: string | null;
    email?: string | null;
    value?: GenerateData | null;
};

@Component({
    selector: 'dmp-search-invoice',
    templateUrl: './search-invoice.component.html',
    styleUrls: ['./search-invoice.component.scss'],
    standalone: false
})
export class SearchInvoiceComponent implements OnInit, OnDestroy {
    get locale(): string { return this.translate.getCurrentLang() === 'en' ? 'en-GB' : 'fr-FR'; }

    protected currentData: CurrentPreviewData | null = null;
    private readonly destroy$ = new Subject<void>();
    private readonly incidentRefresh$ = new Subject<void>();
    incidents: CallInvoiceIncident[] = [];
    incidentsLoading = false;
    incidentsLoadError = false;
    incidentRetryLoading = false;
    retryingIncidentId: string | null = null;
    showResolvedIncidents = false;
    private incidentContext = '';
    private connectedEmail: string | null = null;

    private readonly s3Retry$ = new Subject<string>();
    s3Configured: boolean | null = null;
    s3StatusLoading = false;
    s3StatusError = false;
    private readonly brevoRetry$ = new Subject<string>();
    brevoConfigured: boolean | null = null;
    brevoStatusLoading = false;
    brevoStatusError = false;
    private readonly partnerConfigurationRetry$ = new Subject<void>();
    partnerConfigurations: CallInvoicePartnerConfigurationDto[] = [];
    partnerConfigurationLoading = false;
    partnerConfigurationError = false;
    private readonly periodPreviewRetry$ = new Subject<void>();
    private readonly commandStatusRetry$ = new Subject<void>();
    bookingStatuses: BookingStatusDTO[] = [];
    commandStatusesLoading = false;
    commandStatusesError = false;
    periodPreviews: InvoicePeriodPreview[] = [];
    periodPreviewLoading = false;
    periodPreviewError = false;
    generationLoading = false;
    generationError: string | null = null;
    generationResultAvailable = false;
    settingsExpanded = true;
    advancedExpanded = false;
    calendarPeriods = true;
    savingConfiguration = false;
    saveError: string | null = null;
    private savedConfiguration: string | null = null;
    private restoringConfiguration = false;
    private pendingConfigurationNavigation = false;
    deliveryMode: 'SMTP' | 'BREVO' | 'UNAVAILABLE' | null = null;
    previewVisible = false;
    previewGeneration: CallInvoiceGenerationView | null = null;
    previewAffiliation: CallInvoiceGenerationAffiliationView | null = null;
    activePreviewTab: 'pdf' | 'excel' | 'email' = 'email';
    activeExcelFile: CallInvoiceGenerationFileView | null = null;
    previewLoading = false;
    previewError: string | null = null;
    downloadingZip = false;
    testEmailLoading = false;
    readonly testEmailControl = new FormControl('', [Validators.required, Validators.email]);
    private readonly previewCancel$ = new Subject<void>();


    constructor(
        private readonly translate: TranslateService,
        private utilsService: UtilsService,
        private configuratorSendInBlueService: ConfiguratorSendInBlueService,
        private configuratorSaveQueryService: ConfiguratorSaveQueryService,
        private configuratorCallInvoicesService: ConfiguratorCallInvoicesService,
        private engineSearchInvoiceService: EngineSearchInvoiceService,
        protected readonly route: ActivatedRoute,
        private router: Router,
        private sendingblueService: ConfiguratorSendInBlueService,
        private messageService: MessageService,
        private fb: FormBuilder,
        private sanitizer: DomSanitizer,
        @Inject(DOCUMENT) private readonly document: Document,
        @Optional() private readonly accountService: AccountService = null
    ) {
        this.form = this.fb.group({
            id: [null],
            name: ['', [Validators.required, Validators.pattern(/\S/)]],
            description: [''],
            billingDirection: [null, Validators.required],
            partnerCodes: this.fb.control<string[]>([], [Validators.required, this.minLengthArrayControl(1)]),
            tenant: [null, Validators.required],
            rangeDateStart: [null, Validators.required],
            rangeDateEnd: [null],
            statusCommandsByProduct: this.fb.control<Record<string, string[]>>({}, control => this.validateProductStatuses(control)),
            typeTransaction: ['command', Validators.required],
            typeDate: ['RESERVATION_DATE', Validators.required],
            product: this.fb.control<string[]>([], [Validators.required, this.minLengthArrayControl(1)]),
            sendingblueTemplate: [null, Validators.required],
            invoiceTitle: [this.translate.instant('callInvoiceConfiguration.defaultTitle'), [Validators.required, Validators.pattern(/\S/)]],
            statementSuffix: ['REL', [Validators.required, Validators.pattern(/\S/)]],
            amountMode: ['VENTES', Validators.required],
            billingFrequency: ['M', Validators.required],
            automaticGenerationEnabled: [false],
            alertEmailRecipients: ['', control => this.parseEmailRecipients(control.value).some(email =>
                Validators.email(new FormControl(email)) !== null) ? {email: true} : null],
            bccEmailRecipients: ['', control => this.parseEmailRecipients(control.value).some(email =>
                Validators.email(new FormControl(email)) !== null) ? {email: true} : null],
            invoiceGenerationDayOfMonth: [1, [Validators.min(1), Validators.max(31)]],
        });
        this.form.get('automaticGenerationEnabled').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(enabled => {
            const day = this.form.get('invoiceGenerationDayOfMonth');
            day.setValidators(enabled ? [Validators.required, Validators.min(1), Validators.max(31)] : []);
            day.updateValueAndValidity({emitEvent: false});
        });
        this.form.get('product').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            const control = this.form.get('statusCommandsByProduct');
            const value = this.selectedStatusProducts.reduce((map, product) => ({...map, [product]: control.value?.[product] || []}), {});
            control.setValue(value);
        });
        this.form.get('typeTransaction').valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => this.applyTransactionMode());
    }

    get isLeadBilling(): boolean { return this.form.get('typeTransaction').value === 'lead'; }
    get isProviderBilling(): boolean { return this.form.get('billingDirection').value === 'TENANT_TO_PROVIDER'; }

    private applyTransactionMode(): void {
        const statuses = this.form.get('statusCommandsByProduct');
        const date = this.form.get('typeDate');
        const amount = this.form.get('amountMode');
        if (this.isLeadBilling) {
            statuses.clearValidators();
            statuses.disable({emitEvent: false});
            date.setValue('RESERVATION_DATE', {emitEvent: false});
            date.disable({emitEvent: false});
            amount.disable({emitEvent: false});
        } else {
            statuses.setValidators(control => this.validateProductStatuses(control));
            if (!this.readOnly) {
                statuses.enable({emitEvent: false});
                date.enable({emitEvent: false});
                amount.enable({emitEvent: false});
            }
        }
        statuses.updateValueAndValidity({emitEvent: false});
    }

    rangeDateStart: Date;
    rangeDateEnd: Date;
    datastore: string = null;

    get selectedStatusProducts(): string[] {
        return (this.form.get('product').value || []).map((code: string) => code.toLowerCase());
    }

    productLabel(code: string): string {
        return ({flight: this.translate.instant('invoicing.common.flights'), hotel: this.translate.instant('invoicing.common.hotels'), parking: this.translate.instant('invoicing.common.parkings'), car: this.translate.instant('invoicing.common.cars')})[code] || code;
    }

    statusItemsForProduct(product: string): InvoiceCommandStatusItem[] {
        const selected = this.form.get('statusCommandsByProduct').value?.[product] || [];
        const saved = this.callInvoice?.searchInvoicePrepareData?.statusCommandsByProduct?.[product] || [];
        return invoiceCommandStatusItems(this.bookingStatuses, product, selected, saved,
            (key, params) => this.translate.instant(key, params), this.locale);
    }

    selectedStatuses(product: string): string[] { return this.form.get('statusCommandsByProduct').value?.[product] || []; }

    changeProductStatuses(product: string, statuses: string[]): void {
        const control = this.form.get('statusCommandsByProduct');
        control.setValue({...control.value, [product]: statuses});
        control.markAsDirty(); control.markAsTouched();
    }

    private validateProductStatuses(control: import('@angular/forms').AbstractControl): ValidationErrors | null {
        const products: string[] = this.form?.get('product')?.value || [];
        return products.length && products.every(product => Array.isArray(control.value?.[product.toLowerCase()])
            && control.value[product.toLowerCase()].length > 0) ? null : {productStatuses: true};
    }

    datas: { partner: string; value: GenerateData }[] = [];
    flightSaveQueries: SaveQueryToken[];
    carSaveQueries: SaveQueryToken[];
    hotelSaveQueries: SaveQueryToken[];
    flightSaveQuery: string;
    carSaveQuery: string;
    hotelSaveQuery: string;
    commandCar = false;
    commandHotel = false;
    commandFlight = false;

    public callInvoice: CallInvoicesDTO;
    generatedCallInvoices: CallInvoiceGenerationView[] = [];
    expandedGenerationRows: Record<string, boolean> = {};
    generationActionLoading = false;
    scheduleDialogVisible = false;
    scheduleSendDate: Date | null = null;
    scheduleTargetGeneration: CallInvoiceGenerationView | null = null;
    scheduleMinDate = new Date();
    readOnly = false;

    form!: FormGroup;
    typesDate: SelectItem[];
    typesTransaction: SelectItem[];
    billingDirections: SelectItem[] = [
        {label: this.translate.instant('callInvoiceConfiguration.providers'), value: 'TENANT_TO_PROVIDER'},
        {label: this.translate.instant('callInvoiceConfiguration.partners'), value: 'TENANT_TO_PARTNER'}
    ];
    amountModes: SelectItem[];
    billingFrequencies: SelectItem[];

    pdfUrl?: SafeResourceUrl;
    excelPreviewBlob?: Blob;
    excelPreviewFilename?: string;

    private objectUrl?: string;     // URL brute à révoquer

    templateEmail: GetSmtpTemplateOverviewDTO;
    get email(): string { return this.testEmailControl.value || ''; }
    set email(value: string) { this.testEmailControl.setValue(value); }

    /** Validation personnalisée : Minimum 1 élément pour un FormControl qui contient un tableau */
    private minLengthArrayControl(min: number) {
        return (control: import('@angular/forms').AbstractControl) => {
            const v = control.value;
            if (Array.isArray(v) && v.length >= min) {
                return null;
            }
            const actual = Array.isArray(v) ? v.length : 0;
            return {minLengthArray: {required: min, actual}};
        };
    }

    ngOnInit() {
        this.initList();
        this.accountService?.getAuthenticationState().pipe(filter(account => !!account?.email), take(1), takeUntil(this.destroy$)).subscribe(account => {
            this.connectedEmail = account.email;
            this.applyDefaultEmailRecipients();
        });
        merge(this.form.get('id').valueChanges, this.form.get('tenant').valueChanges, this.incidentRefresh$).pipe(
            startWith(null), switchMap(() => {
                const {id, tenant} = this.form.getRawValue();
                const context = `${tenant || ''}/${id || ''}`;
                if (context !== this.incidentContext) { this.incidents = []; this.incidentContext = context; }
                this.incidentsLoadError = false;
                this.incidentsLoading = !!id && !!tenant;
                return this.incidentsLoading ? this.configuratorCallInvoicesService.getIncidents(tenant, id).pipe(
                    catchError(() => { this.incidentsLoadError = true; return of(this.incidents); })) : of([]);
            }), takeUntil(this.destroy$)
        ).subscribe(incidents => { this.incidents = incidents; this.incidentsLoading = false; });
        this.datastore = this.route.snapshot.paramMap.get('tenant');
        let previousTenant = this.form.get('tenant').value;
        this.form.get('tenant')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(tenant => {
            const changedGroup = previousTenant && previousTenant !== tenant;
            previousTenant = tenant;
            if (this.restoringConfiguration) { return; }
            this.datastore = tenant;
            this.form.get('partnerCodes')?.setValue([]);
            if (changedGroup) { this.form.get('statusCommandsByProduct')?.setValue({}); }
            if (changedGroup) { this.form.get('sendingblueTemplate')?.setValue(null); }
        });
        const tenantControl = this.form.get('tenant');
        merge(tenantControl.valueChanges, this.form.get('typeTransaction').valueChanges, this.commandStatusRetry$).pipe(
            startWith(null), switchMap(() => {
                const tenant = tenantControl.value;
                this.bookingStatuses = [];
                this.commandStatusesError = false;
                this.commandStatusesLoading = !!tenant && !this.isLeadBilling;
                return this.commandStatusesLoading ? this.engineSearchInvoiceService.listBookingStatuses(tenant).pipe(
                    catchError(() => { this.commandStatusesError = true; return of([]); })) : of([]);
            }), takeUntil(this.destroy$)
        ).subscribe(statuses => { this.bookingStatuses = statuses; this.commandStatusesLoading = false; });
        merge(tenantControl.valueChanges, this.brevoRetry$).pipe(
            startWith(tenantControl.value),
            switchMap(tenant => {
                this.deliveryMode = null;
                return tenant ? this.configuratorCallInvoicesService.getDeliveryMode(tenant).pipe(catchError(() => of(null))) : of(null);
            }), takeUntil(this.destroy$)
        ).subscribe(result => this.deliveryMode = result?.mode || null);
        merge(tenantControl.valueChanges, this.s3Retry$).pipe(
            startWith(tenantControl.value),
            switchMap((tenant: string | null) => {
                this.s3Configured = null;
                this.s3StatusError = false;
                this.s3StatusLoading = !!tenant;
                if (!tenant) {
                    return of(null);
                }
                return this.configuratorCallInvoicesService.getS3ConfigurationStatusUsingGET(tenant).pipe(
                    catchError(() => {
                        this.s3StatusError = true;
                        return of(null);
                    })
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe(configured => {
            this.s3Configured = configured;
            this.s3StatusLoading = false;
        });
        merge(tenantControl.valueChanges, this.brevoRetry$).pipe(
            startWith(tenantControl.value),
            switchMap((tenant: string | null) => {
                this.brevoConfigured = null;
                this.brevoStatusError = false;
                this.brevoStatusLoading = !!tenant;
                if (!tenant) {
                    return of(null);
                }
                return this.configuratorCallInvoicesService.getBrevoConfigurationStatusUsingGET(tenant).pipe(
                    catchError(() => {
                        this.brevoStatusError = true;
                        return of(null);
                    })
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe(configured => {
            this.brevoConfigured = configured;
            this.brevoStatusLoading = false;
        });
        const id = this.route.snapshot.paramMap.get('id');
        const partnerControl = this.form.get('partnerCodes');
        merge(tenantControl.valueChanges, partnerControl.valueChanges, this.partnerConfigurationRetry$).pipe(
            startWith(null),
            switchMap(() => {
                this.partnerConfigurations = [];
                this.partnerConfigurationError = false;
                const tenant = tenantControl.value;
                const partnerCodes: string[] = partnerControl.value || [];
                this.partnerConfigurationLoading = !!tenant && partnerCodes.length > 0;
                if (!this.partnerConfigurationLoading) {
                    return of([] as CallInvoicePartnerConfigurationDto[]);
                }
                return this.configuratorCallInvoicesService.getPartnerConfigurationStatusUsingGET(tenant, partnerCodes).pipe(
                    catchError(() => {
                        this.partnerConfigurationError = true;
                        return of([] as CallInvoicePartnerConfigurationDto[]);
                    })
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe(statuses => {
            this.partnerConfigurations = statuses;
            this.partnerConfigurationLoading = false;
            const selected: string[] = partnerControl.value || [];
            if (tenantControl.value && selected.some(code => !statuses.some(status => status.partnerCode === code))) {
                this.partnerConfigurationError = true;
            }
        });
        merge(...['id', 'tenant', 'partnerCodes', 'rangeDateStart', 'rangeDateEnd', 'billingFrequency', 'product', 'typeTransaction', 'billingDirection', 'statusCommandsByProduct', 'typeDate']
            .map(name => this.form.get(name).valueChanges), this.periodPreviewRetry$).pipe(
            startWith(null),
            switchMap(() => {
                this.periodPreviews = [];
                this.periodPreviewError = false;
                this.periodPreviewLoading = this.canPreviewPeriods && !this.periodDateRangeInvalid;
                if (!this.periodPreviewLoading) {
                    return of([] as InvoicePeriodPreview[]);
                }
                const {id: invoiceId, partnerCodes, rangeDateStart, rangeDateEnd, billingFrequency, tenant, product, typeTransaction, billingDirection, statusCommandsByProduct, typeDate} = this.form.getRawValue();
                const request = {id: invoiceId, partnerCodes, rangeDateStart, rangeDateEnd, billingFrequency, product, typeTransaction, billingDirection, statusCommandsByProduct, typeDate};
                return this.engineSearchInvoiceService.previewPeriods(request, tenant).pipe(catchError(() => {
                    this.periodPreviewError = true;
                    return of([] as InvoicePeriodPreview[]);
                }));
            }),
            takeUntil(this.destroy$)
        ).subscribe(periods => {
            this.periodPreviews = periods;
            if (this.periodPreviewLoading && periods.some(period =>
                !period.partners || this.form.get('partnerCodes').value.some(code =>
                    !period.partners.some(partner => partner.partnerCode === code)))) {
                this.periodPreviewError = true;
            }
            this.periodPreviewLoading = false;
        });
        if (id) {
            this.loadCallInvoice(id);
        }
        const returnEvents = [fromEvent(this.document, 'visibilitychange')];
        if (this.document.defaultView) {
            returnEvents.push(fromEvent(this.document.defaultView, 'focus'));
        }
        merge(...returnEvents).pipe(
            debounceTime(100),
            filter(() => this.document.visibilityState === 'visible'),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            if (!this.form.get('tenant')?.value) { return; }
            this.retryS3Configuration();
            this.retryBrevoConfiguration();
            this.retryPartnerConfiguration();
            this.retryCommandStatuses();
            this.refreshIncidents();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.s3Retry$.complete();
        this.brevoRetry$.complete();
        this.partnerConfigurationRetry$.complete();
        this.periodPreviewRetry$.complete();
        this.commandStatusRetry$.complete();
        this.incidentRefresh$.complete();
        this.resetPreviews();
        this.previewCancel$.complete();
    }

    retryS3Configuration(): void {
        this.s3Retry$.next(this.form.get('tenant')?.value);
    }

    retryBrevoConfiguration(): void {
        this.brevoRetry$.next(this.form.get('tenant')?.value);
    }

    retryPartnerConfiguration(): void {
        this.partnerConfigurationRetry$.next();
        this.retryPeriodPreview();
    }

    retryPeriodPreview(): void {
        this.periodPreviewRetry$.next();
    }

    retryCommandStatuses(): void {
        this.commandStatusRetry$.next();
    }

    get canPreviewPeriods(): boolean {
        const value = this.form.getRawValue();
        return !!value.tenant && value.partnerCodes?.length > 0 && !!value.rangeDateStart
            && ['M', 'T'].includes(value.billingFrequency);
    }

    get periodDateRangeInvalid(): boolean {
        const {rangeDateStart, rangeDateEnd} = this.form.getRawValue();
        if (!rangeDateStart) { return false; }
        const start = new Date(rangeDateStart);
        const end = rangeDateEnd ? new Date(rangeDateEnd) : new Date();
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        return Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start;
    }

    get periodProblemCount(): number {
        return this.periodPreviews.filter(period => !period.alreadyGenerated && !period.valid).length;
    }

    get readyPeriodCount(): number { return this.periodPreviews.filter(period => !period.alreadyGenerated && period.valid).length; }
    get alreadyGeneratedPeriodCount(): number { return this.periodPreviews.filter(period => period.alreadyGenerated).length; }

    get periodPreviewValid(): boolean {
        return this.canPreviewPeriods && !this.periodDateRangeInvalid && !this.periodPreviewLoading
            && !this.periodPreviewError && this.readyPeriodCount > 0
            && this.periodPreviews.every(period => period.alreadyGenerated || period.valid);
    }

    get hasGeneratedDocuments(): boolean {
        return this.generatedCallInvoices.length > 0 || this.datas.length > 0;
    }

    get generationResultKey(): string | null {
        if (!this.generationResultAvailable || this.generationError !== null) {
            return null;
        }
        if (this.callInvoice?.errorCommands?.length) {
            return this.isLeadBilling ? 'callInvoiceGeneration.excludedLeads' : 'callInvoiceGeneration.excluded';
        }
        return this.hasGeneratedDocuments ? 'callInvoiceGeneration.completed' : 'callInvoiceGeneration.empty';
    }

    get requiresPartnerInvoicingContacts(): boolean {
        return !this.isProviderBilling;
    }

    get partnerConfigurationIssues(): CallInvoicePartnerConfigurationDto[] {
        return this.partnerConfigurations.filter(status => !status.companyConfigured
            || (this.requiresPartnerInvoicingContacts && !status.invoicingContactConfigured));
    }

    get partnerConfigurationValid(): boolean {
        const selected: string[] = this.form.get('partnerCodes')?.value || [];
        return !this.partnerConfigurationLoading && !this.partnerConfigurationError && selected.length > 0
            && selected.every(code => this.partnerConfigurations.some(status => status.partnerCode === code
                && status.companyConfigured && (!this.requiresPartnerInvoicingContacts || status.invoicingContactConfigured)));
    }

    private applyDefaultEmailRecipients(): void {
        // Defaults belong to creation; loading an existing call must preserve its saved recipients.
        if (this.form.get('id').value || this.route.snapshot.paramMap.get('id')) { return; }
        for (const name of ['alertEmailRecipients', 'bccEmailRecipients']) {
            const control = this.form.get(name);
            if (this.connectedEmail && control.pristine && !control.value) {
                control.setValue(this.connectedEmail, {emitEvent: false});
            }
        }
    }

    get savedBccRecipients(): string[] { return this.callInvoice?.searchInvoicePrepareData?.bccEmailRecipients || []; }

    private parseEmailRecipients(value: string | null): string[] {
        return [...new Set((value || '').split(/[\s;,]+/).map(email => email.trim().toLowerCase()).filter(Boolean))];
    }

    refreshIncidents(): void { this.incidentRefresh$.next(); }

    get openIncidentsCount(): number { return this.incidents.filter(incident => incident.status !== 'RESOLVED').length; }

    get visibleIncidents(): CallInvoiceIncident[] { return this.incidents.filter(incident => this.showResolvedIncidents || incident.status !== 'RESOLVED'); }

    partialDocumentsRemain(incident: CallInvoiceIncident): boolean {
        return incident.reasonCode === 'PARTIAL_GENERATION' && this.generatedCallInvoices.some(generation =>
            generation.billingFrequency === incident.billingFrequency && generation.rangeDateStart instanceof Date && generation.rangeDateEnd instanceof Date
            && this.formatDateForApi(generation.rangeDateStart) === incident.rangeDateStart && this.formatDateForApi(generation.rangeDateEnd) === incident.rangeDateEnd);
    }

    retryIncident(incident: CallInvoiceIncident): void {
        if (this.incidentRetryLoading || this.generationLoading || this.savingConfiguration || this.hasUnsavedChanges || incident.status !== 'OPEN' || this.partialDocumentsRemain(incident)) { return; }
        this.incidentRetryLoading = true;
        this.retryingIncidentId = incident.id;
        this.configuratorCallInvoicesService.retryIncident(this.datastore, this.callInvoice.id, incident.id).pipe(
            finalize(() => { this.incidentRetryLoading = false; this.retryingIncidentId = null; }), takeUntil(this.destroy$)
        ).subscribe({next: result => {
            this.refreshIncidents(); this.retryPeriodPreview();
            this.loadCallInvoice(this.callInvoice.id);
            this.messageService.add({severity: result.status === 'RESOLVED' ? 'success' : 'warn', summary: this.translate.instant('callInvoiceIncidents.retryTitle'),
                detail: result.status === 'RESOLVED' ? result.outcome === 'NO_DATA' ? this.translate.instant('callInvoiceIncidents.noData') : this.translate.instant('callInvoiceIncidents.documentsReady')
                    : this.translate.instant('callInvoiceIncidents.stillBlocked')});
        }, error: error => {
            this.refreshIncidents();
            this.messageService.add({severity: 'error', summary: this.translate.instant('callInvoiceIncidents.retryFailed'), detail: error?.error?.detail || error?.error?.title || this.translate.instant('callInvoiceIncidents.retryFailedDetail')});
        }});
    }

    get hasUnsavedChanges(): boolean {
        return this.savedConfiguration === null ? this.form.dirty : this.configurationFingerprint() !== this.savedConfiguration;
    }

    get canGenerate(): boolean {
        return this.form.valid && !this.savingConfiguration && !this.generationLoading && !this.incidentRetryLoading
            && !this.s3StatusLoading && this.s3Configured === true
            && !this.brevoStatusLoading && this.brevoConfigured === true
            && this.partnerConfigurationValid && this.periodPreviewValid;
    }

    get invalidFields(): {name: string; label: string}[] {
        return Object.entries(CALL_INVOICE_FIELDS).filter(([name]) => this.form.get(name)?.invalid)
            .map(([name, field]) => ({name, label: this.translate.instant(field.label)}));
    }

    fieldError(name: string): string | null {
        const control = this.form.get(name);
        return control?.invalid && (control.touched || control.dirty)
            ? this.errorsToMessages(control.errors).join('. ') : null;
    }

    focusField(name: string): void {
        this.settingsExpanded = true;
        if (CALL_INVOICE_FIELDS[name]?.section === 'advanced') { this.advancedExpanded = true; }
        // Wait for the expanded settings and custom controls to be rendered.
        setTimeout(() => {
            const field = this.document.getElementById(`field-${name}`);
            field?.scrollIntoView({behavior: 'smooth', block: 'center'});
            (field?.querySelector('input, select, button, [tabindex="0"]') as HTMLElement)?.focus();
        });
    }

    get preparationChecks(): {label: string; ready: boolean; loading: boolean; detail: string; field: string}[] {
        return [
            {label: this.translate.instant('callInvoiceConfiguration.parameters'), ready: this.form.valid && !this.periodDateRangeInvalid, loading: false, field: this.invalidFields[0]?.name || 'rangeDateStart',
                detail: this.invalidFields.length ? this.translate.instant('callInvoiceConfiguration.fieldsToComplete', {count: this.invalidFields.length}) : this.periodDateRangeInvalid ? this.translate.instant('callInvoiceConfiguration.endBeforeStart') : this.translate.instant('callInvoiceConfiguration.parametersComplete')},
            {label: this.translate.instant('callInvoiceConfiguration.documentStorage'), ready: this.s3Configured === true, loading: this.s3StatusLoading, field: 'tenant',
                detail: this.s3StatusError ? this.translate.instant('callInvoiceConfiguration.checkFailed') : this.s3Configured === true ? this.translate.instant('callInvoiceConfiguration.storageConfigured') : this.translate.instant('callInvoiceConfiguration.checkGroup')},
            {label: this.translate.instant('callInvoiceConfiguration.emailService'), ready: this.brevoConfigured === true && this.form.get('sendingblueTemplate').valid,
                loading: this.brevoStatusLoading, field: 'sendingblueTemplate', detail: this.brevoStatusError ? this.translate.instant('callInvoiceConfiguration.checkFailed') : this.brevoConfigured === true ? (this.form.get('sendingblueTemplate').valid ? this.translate.instant('callInvoiceConfiguration.emailConfigured') : this.translate.instant('callInvoiceConfiguration.chooseTemplate')) : this.translate.instant('callInvoiceConfiguration.checkEmail')},
            {label: this.translate.instant('callInvoiceConfiguration.recordsAndContacts'), ready: this.partnerConfigurationValid, loading: this.partnerConfigurationLoading, field: 'partnerCodes',
                detail: this.partnerConfigurationError ? this.translate.instant('callInvoiceConfiguration.checkFailed') : this.partnerConfigurationValid ? this.translate.instant('callInvoiceConfiguration.affiliatesConfigured') : this.translate.instant('callInvoiceConfiguration.completePartners')},
            {label: this.translate.instant(this.isLeadBilling ? 'callInvoiceConfiguration.leadChecks' : this.isProviderBilling ? 'callInvoicePeriods.providersCheck' : 'callInvoiceConfiguration.periodChecks'), ready: this.periodPreviewValid, loading: this.periodPreviewLoading, field: 'rangeDateStart',
                detail: this.periodPreviewError ? this.translate.instant('callInvoiceConfiguration.checkFailed') : this.periodProblemCount ? this.translate.instant('callInvoicePeriods.blockedCount', {count: this.periodProblemCount}) : this.readyPeriodCount ? this.translate.instant('callInvoicePeriods.newCount', {count: this.readyPeriodCount}) : this.translate.instant('callInvoiceConfiguration.noReadyPeriods')}
        ];
    }

    get automationDescription(): string {
        if (!this.form.get('automaticGenerationEnabled').value) { return this.translate.instant('callInvoiceConfiguration.manualDescription'); }
        const day = this.form.get('invoiceGenerationDayOfMonth').value || '…';
        return this.form.get('billingFrequency').value === 'T'
            ? this.translate.instant('callInvoiceConfiguration.quarterlyDescription', {day})
            : this.translate.instant('callInvoiceConfiguration.monthlyDescription', {day});
    }

    get lastClosedPeriodLabel(): string {
        const now = new Date();
        const end = this.form.get('billingFrequency').value === 'T'
            ? new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 0)
            : new Date(now.getFullYear(), now.getMonth(), 0);
        return this.form.get('billingFrequency').value === 'T'
            ? this.translate.instant('callInvoicePeriods.quarterShort', {quarter: Math.floor(end.getMonth() / 3) + 1, year: end.getFullYear()})
            : end.toLocaleDateString(this.locale, {month: 'long', year: 'numeric'});
    }

    changePeriodMode(): void {
        this.calendarPeriods = !this.calendarPeriods;
        if (this.calendarPeriods) { this.normalizeCalendarPeriods(); }
    }

    normalizeCalendarPeriods(): void {
        if (!this.calendarPeriods) { return; }
        const quarterly = this.form.get('billingFrequency').value === 'T';
        for (const name of ['rangeDateStart', 'rangeDateEnd']) {
            const value = this.form.get(name).value;
            if (!value) { continue; }
            const date = new Date(value);
            const month = quarterly ? Math.floor(date.getMonth() / 3) * 3 : date.getMonth();
            this.form.get(name).setValue(name === 'rangeDateStart'
                ? new Date(date.getFullYear(), month, 1)
                : new Date(date.getFullYear(), month + (quarterly ? 3 : 1), 0));
        }
        this.form.markAsDirty();
    }

    private configurationFingerprint(): string { return JSON.stringify(this.form.getRawValue()); }

    private rememberConfiguration(): void {
        this.savedConfiguration = this.configurationFingerprint();
        this.form.markAsPristine();
    }

    private lockPersistedFields(configuration: Pick<SearchInvoicePrepareDataVO, 'tenant' | 'billingDirection' | 'typeTransaction'>): void {
        if (!this.form.get('id').value) { return; }
        for (const name of ['tenant', 'billingDirection', 'typeTransaction'] as const) {
            const control = this.form.get(name);
            // Keep the persisted values even if the form changed while saving.
            control.setValue(configuration[name] ?? null, {emitEvent: false});
            control.disable({emitEvent: false});
        }
        this.applyTransactionMode();
    }

    get billingDirectionLabel(): string {
        return this.billingDirections.find(option => option.value === this.form.controls.billingDirection.value)?.label
            || this.form.controls.billingDirection.value;
    }

    get transactionTypeLabel(): string {
        return this.typesTransaction?.find(option => option.value === this.form.controls.typeTransaction.value)?.label
            || this.form.controls.typeTransaction.value;
    }

    private configurationPayload(): SearchInvoicePrepareDataVO {
        const value = this.form.getRawValue();
        return {
            ...value,
            ...(this.isLeadBilling ? {typeDate: 'RESERVATION_DATE', statusCommandsByProduct: {}, amountMode: 'VENTES'} : {}),
            name: value.name?.trim(), invoiceTitle: value.invoiceTitle?.trim(), statementSuffix: value.statementSuffix?.trim(),
            invoiceGenerationDayOfMonth: value.automaticGenerationEnabled ? Number(value.invoiceGenerationDayOfMonth) : null,
            remunerations: this.callInvoice?.searchInvoicePrepareData?.remunerations || [],
            alertEmailRecipients: this.parseEmailRecipients(value.alertEmailRecipients),
            bccEmailRecipients: this.parseEmailRecipients(value.bccEmailRecipients)
        };
    }

    saveConfiguration(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid || this.periodDateRangeInvalid) {
            this.focusField(this.invalidFields[0]?.name || 'rangeDateStart');
            return;
        }
        if (this.savingConfiguration || this.generationLoading || this.incidentRetryLoading) { return; }
        const payload = this.configurationPayload();
        const submitted = this.configurationFingerprint();
        this.saveError = null;
        this.savingConfiguration = true;
        this.configuratorCallInvoicesService.saveConfiguration(payload.tenant, payload).pipe(
            finalize(() => this.savingConfiguration = false), takeUntil(this.destroy$)
        ).subscribe({next: result => {
            this.callInvoice = result;
            this.datastore = payload.tenant;
            this.form.get('id').setValue(result.id, {emitEvent: false});
            this.lockPersistedFields(payload);
            // Do not silently mark edits made during the request as saved.
            this.savedConfiguration = JSON.stringify({...JSON.parse(submitted), id: result.id});
            if (!this.hasUnsavedChanges) { this.form.markAsPristine(); }
            this.updateGenerationResults();
            this.refreshIncidents();
            this.retryPeriodPreview();
            this.messageService.add({severity: 'success', summary: this.translate.instant('callInvoiceConfiguration.saved'), detail: this.translate.instant('callInvoiceConfiguration.savedDetail')});
            this.navigateToSavedConfiguration(payload.tenant, result.id, !payload.id);
        }, error: err => {
            this.saveError = err?.error?.detail || err?.error?.message || this.translate.instant('callInvoiceConfiguration.saveFailed');
        }});
    }

    private navigateToSavedConfiguration(tenant: string, id: string, created: boolean): void {
        this.pendingConfigurationNavigation = this.pendingConfigurationNavigation || created;
        if (id && this.pendingConfigurationNavigation && !this.hasUnsavedChanges) {
            this.pendingConfigurationNavigation = false;
            this.router.navigate([`/h/apps/call-for-invoicing/view/${tenant}/${id}`]);
        }
    }

    /** Formatage lisible des erreurs */
    private errorsToMessages(errors: ValidationErrors): string[] {
        const messages: string[] = [];
        for (const [key, info] of Object.entries(errors)) {
            switch (key) {
                case 'productStatuses':
                    messages.push(this.translate.instant('invoicingValidation.productStatuses'));
                    break;
                case 'required':
                    messages.push(this.translate.instant('invoicingValidation.required'));
                    break;
                case 'requiredByMode':
                    messages.push(this.translate.instant('invoicingValidation.required'));
                    break;
                case 'minlength':
                    messages.push(this.translate.instant('invoicingValidation.minLength', {required: info.requiredLength, actual: info.actualLength}));
                    break;
                case 'maxlength':
                    messages.push(this.translate.instant('invoicingValidation.maxLength', {required: info.requiredLength, actual: info.actualLength}));
                    break;
                case 'min':
                    messages.push(this.translate.instant('invoicingValidation.min', {required: info.min, actual: info.actual}));
                    break;
                case 'max':
                    messages.push(this.translate.instant('invoicingValidation.max', {required: info.max, actual: info.actual}));
                    break;
                case 'pattern':
                    messages.push(this.translate.instant('invoicingValidation.textRequired'));
                    break;
                case 'calendarYear':
                    messages.push(this.translate.instant('invoicingValidation.calendarYear'));
                    break;
                case 'email':
                    messages.push(this.translate.instant('invoicingValidation.invalidEmail'));
                    break;
                case 'minLengthArray': {
                    // ton validateur custom peut renvoyer soit true, soit un objet détaillé
                    const required = typeof info === 'object' && info?.required ? info.required : 1;
                    const actual = typeof info === 'object' && info?.actual != null ? info.actual : 'n/a';
                    messages.push(this.translate.instant('invoicingValidation.minItems', {required, actual}));
                    break;
                }
                default:
                    messages.push(`${key}: ${JSON.stringify(info)}`);
            }
        }
        return messages;
    }

    generateInvoiceData() {
        this.form.markAllAsTouched();
        if (!this.canGenerate) {
            return;
        }
        const value = this.form.getRawValue();
        const payload = this.configurationPayload();
        const submitted = this.configurationFingerprint();
        this.generationLoading = true;
        this.generationError = null;
        this.generationResultAvailable = false;
        this.engineSearchInvoiceService.prepareData(payload, value.tenant).pipe(
            finalize(() => this.generationLoading = false), takeUntil(this.destroy$)
        ).subscribe({next: (res) => {
                this.datastore = value.tenant;
                this.callInvoice = res;
                this.updateGenerationResults();
                this.form.get('id').setValue(res.id || payload.id, {emitEvent: false});
                this.lockPersistedFields(payload);
                this.savedConfiguration = JSON.stringify({...JSON.parse(submitted), id: res.id || payload.id});
                this.generationResultAvailable = true;
                this.generationError = res.error?.trim() || null;
                this.retryPeriodPreview();
                this.refreshIncidents();
                this.navigateToSavedConfiguration(value.tenant, res.id, !payload.id);
            }, error: err => {
                this.retryPeriodPreview();
                this.generationError = err?.error?.detail || err?.error?.message || '';
            }});
    }

    private init() {
        this.updateGenerationResults();
        this.generationResultAvailable = !!this.callInvoice?.dateTimeEnd;
        this.generationError = this.callInvoice?.error?.trim() || null;
        if (!this.callInvoice || !this.callInvoice.searchInvoicePrepareData) {
            return;
        }
        if (this.savedConfiguration !== null && this.hasUnsavedChanges) { return; }
        const s: SearchInvoicePrepareDataDTO = this.callInvoice.searchInvoicePrepareData;
        const searchDataWithFilters = s as SearchInvoicePrepareDataDTO & SearchInvoicePersonalization;

        // Patch des champs simples
        this.restoringConfiguration = true;
        this.datastore = s.tenant;
        this.form.patchValue({
            id: this.callInvoice.id,
            name: searchDataWithFilters.name ?? '',
            description: searchDataWithFilters.description ?? '',
            billingDirection: searchDataWithFilters.billingDirection ?? null,
            tenant: s.tenant,
            partnerCodes: searchDataWithFilters.partnerCodes ?? [],
            rangeDateStart: s.rangeDateStart ? new Date(s.rangeDateStart) : null,
            rangeDateEnd: s.rangeDateEnd ? new Date(s.rangeDateEnd) : null,
            statusCommandsByProduct: searchDataWithFilters.statusCommandsByProduct || {},
            typeTransaction: s.typeTransaction,
            typeDate: s.typeDate,
            product: s.product,
            sendingblueTemplate: s.sendingblueTemplate,
            invoiceTitle: searchDataWithFilters.invoiceTitle ?? null,
            statementSuffix: searchDataWithFilters.statementSuffix ?? null,
            amountMode: searchDataWithFilters.amountMode ?? null,
            billingFrequency: searchDataWithFilters.billingFrequency ?? null,
            automaticGenerationEnabled: searchDataWithFilters.automaticGenerationEnabled ?? true,
            alertEmailRecipients: (s.alertEmailRecipients || []).join(', '),
            bccEmailRecipients: (s.bccEmailRecipients || []).join(', '),
            invoiceGenerationDayOfMonth: searchDataWithFilters.invoiceGenerationDayOfMonth ?? 1,
        });
        this.restoringConfiguration = false;
        this.applyTransactionMode();
        this.lockPersistedFields(s);
        this.settingsExpanded = false;
        this.calendarPeriods = this.hasCalendarBounds();
        this.rememberConfiguration();
    }

    private hasCalendarBounds(): boolean {
        const {rangeDateStart: start, rangeDateEnd: end, billingFrequency: frequency} = this.form.value;
        const quarter = frequency === 'T';
        return (!start || (start.getDate() === 1 && (!quarter || start.getMonth() % 3 === 0)))
            && (!end || (end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
                && (!quarter || end.getMonth() % 3 === 2)));
    }

    private updateGenerationResults(): void {
        this.generatedCallInvoices = this.extractGeneratedCallInvoices(this.callInvoice);
        this.expandedGenerationRows = {};
        this.datas = [];

        // tslint:disable-next-line:forin
        for (const key in (this.callInvoice?.generateDatasParAffi || {})) {
            const value = this.callInvoice.generateDatasParAffi[key];
            this.datas.push({
                partner: key,
                value
            });
        }
    }

    private loadCallInvoice(id: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.configuratorCallInvoicesService
                .getCallInvoicesUsingGET(this.datastore, true, id)
                .subscribe(res => {
                    this.callInvoice = res;
                    this.init();
                    this.refreshIncidents();
                    resolve();
                });
        });
    }

    private extractGeneratedCallInvoices(callInvoice: CallInvoicesDTO): CallInvoiceGenerationView[] {
        const generations = ((callInvoice as unknown as { generatedCallInvoices?: CallInvoiceGenerationView[] })
            ?.generatedCallInvoices || [])
            .filter((generation) => !!generation)
            .map((generation, index) => {
                const normalizedAffiliationDetails = this.normalizeAffiliationDetails(generation);
                const normalizedAffiliations = normalizedAffiliationDetails
                    .map((detail) => detail.affiliation || '')
                    .filter((affiliation) => !!affiliation);
                return {
                    ...generation,
                    rowKey: this.buildGenerationRowKey(generation, index),
                    rangeDateStart: this.parseGenerationDate(generation.rangeDateStart),
                    rangeDateEnd: this.parseGenerationDate(generation.rangeDateEnd),
                    generatedAt: this.parseGenerationDate(generation.generatedAt),
                    validatedAt: this.parseGenerationDate(generation.validatedAt),
                    sendedAt: this.parseGenerationDate(generation.sendedAt),
                    sentAt: this.parseGenerationDate(generation.sentAt),
                    plannedSendDate: this.parseGenerationDate(generation.plannedSendDate),
                    affiliationDetails: normalizedAffiliationDetails,
                    affiliations: normalizedAffiliations,
                    sendSplitActions: this.buildSendSplitActions(generation)
                };
            });

        return generations.sort(
            (a, b) => this.toTimestamp(b.generatedAt) - this.toTimestamp(a.generatedAt)
        );
    }

    private normalizeAffiliationDetails(generation: CallInvoiceGenerationView): CallInvoiceGenerationAffiliationView[] {
        const rawDetails = (generation as unknown as { affiliationDetails?: unknown[] })?.affiliationDetails;
        if (Array.isArray(rawDetails) && rawDetails.length > 0) {
            return rawDetails
                .filter((item) => !!item)
                .map((item) => {
                    const detail = item as {
                        affiliation?: string;
                        clientName?: string;
                        status?: string | null;
                        errorMsg?: string | null;
                        invoiceMail?: { email?: string; clientName?: string };
                        pdfFile?: CallInvoiceGenerationFileView | null;
                        excelFiles?: CallInvoiceGenerationFileView[] | null;
                    };
                    const normalizedExcelFiles = Array.isArray(detail.excelFiles)
                        ? detail.excelFiles
                            .map((file) => this.normalizeGenerationFile(file))
                            .filter((file): file is CallInvoiceGenerationFileView => !!file)
                        : [];

                    const affiliationView: CallInvoiceGenerationAffiliationView = {
                        affiliation: detail.affiliation || null,
                        clientName: detail.clientName || detail.invoiceMail?.clientName || null,
                        status: detail.status || null,
                        errorMsg: detail.errorMsg || null,
                        invoiceMail: detail.invoiceMail || null,
                        pdfFile: this.normalizeGenerationFile(detail.pdfFile),
                        excelFiles: normalizedExcelFiles
                    };

                    affiliationView.excelFiles = normalizedExcelFiles.map((excelFile) => ({
                        ...excelFile,
                        splitActions: this.buildExcelSplitActions(generation, affiliationView, excelFile)
                    }));

                    return affiliationView;
                });
        }

        if (Array.isArray(generation.affiliations)) {
            return generation.affiliations
                .filter((affiliation) => !!affiliation)
                .map((affiliation) => ({
                    affiliation,
                    clientName: null,
                    invoiceMail: null,
                    pdfFile: null,
                    excelFiles: []
                }));
        }
        return [];
    }

    private normalizeGenerationFile(file?: CallInvoiceGenerationFileView | null): CallInvoiceGenerationFileView | null {
        if (!file) {
            return null;
        }
        return {
            id: file.id || null,
            productType: file.productType || null,
            fileName: file.fileName || null,
            filePath: file.filePath || null,
            contentType: file.contentType || null
        };
    }

    getGenerationAffiliationDetails(generation?: CallInvoiceGenerationView | null): CallInvoiceGenerationAffiliationView[] {
        if (!generation?.affiliationDetails || !Array.isArray(generation.affiliationDetails)) {
            return [];
        }
        return generation.affiliationDetails;
    }

    getGenerationAffiliationCount(generation?: CallInvoiceGenerationView | null): number {
        return this.getGenerationAffiliationDetails(generation).length;
    }

    isGenerationExpanded(generation?: CallInvoiceGenerationView | null): boolean {
        const key = generation?.rowKey;
        if (!key) {
            return false;
        }
        return !!this.expandedGenerationRows[key];
    }

    toggleGenerationRow(generation?: CallInvoiceGenerationView | null, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        const key = generation?.rowKey;
        if (!key) {
            return;
        }

        if (this.expandedGenerationRows[key]) {
            const cloned = {...this.expandedGenerationRows};
            delete cloned[key];
            this.expandedGenerationRows = cloned;
            return;
        }

        this.expandedGenerationRows = {
            ...this.expandedGenerationRows,
            [key]: true
        };
    }

    private buildGenerationRowKey(generation: CallInvoiceGenerationView, index: number): string {
        if (generation?.id) {
            return generation.id;
        }
        const start = this.toTimestamp(generation?.rangeDateStart);
        const end = this.toTimestamp(generation?.rangeDateEnd);
        const generatedAt = this.toTimestamp(generation?.generatedAt);
        const frequency = generation?.billingFrequency || 'NA';
        return `${frequency}-${start}-${end}-${generatedAt}-${index}`;
    }

    private toTimestamp(dateValue?: string | Date | number[] | null): number {
        const value = this.parseGenerationDate(dateValue);
        if (!value) {
            return 0;
        }
        return value.getTime();
    }

    protected parseGenerationDate(dateValue?: string | Date | number[] | null): Date | null {
        if (!dateValue) {
            return null;
        }

        if (dateValue instanceof Date) {
            return isNaN(dateValue.getTime()) ? null : dateValue;
        }

        if (Array.isArray(dateValue)) {
            return this.fromJavaDateArray(dateValue);
        }

        if (typeof dateValue === 'number') {
            return new Date(dateValue);
        }

        if (typeof dateValue === 'string') {
            const trimmed = dateValue.trim();
            if (!trimmed) {
                return null;
            }
            if (trimmed.includes(',')) {
                const parts = trimmed
                    .split(',')
                    .map((part) => Number(part.trim()))
                    .filter((part) => !isNaN(part));
                const parsedCommaDate = this.fromJavaDateArray(parts);
                if (parsedCommaDate) {
                    return parsedCommaDate;
                }
            }
            const parsed = new Date(trimmed);
            return isNaN(parsed.getTime()) ? null : parsed;
        }

        return null;
    }

    private fromJavaDateArray(parts: number[]): Date | null {
        if (!Array.isArray(parts) || parts.length < 3) {
            return null;
        }

        const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = parts;
        if ([year, month, day, hour, minute, second, nano].some((value) => typeof value !== 'number' || isNaN(value))) {
            return null;
        }

        const millisecond = Math.floor(nano / 1000000);
        const parsed = new Date(year, month - 1, day, hour, minute, second, millisecond);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    getGenerationFrequencyLabel(frequency?: string | null): string {
        if (frequency === 'T') {
            return this.translate.instant('invoicing.common.quarterly');
        }
        if (frequency === 'M') {
            return this.translate.instant('invoicing.common.monthly');
        }
        return frequency || '-';
    }

    getGenerationStatusLabel(status?: string | null): string {
        if (status === 'SEND') {
            return this.translate.instant('callInvoiceGeneration.sent');
        }
        if (status === 'PLANIFIEE') {
            return this.translate.instant('callInvoiceGeneration.scheduled');
        }
        if (status === 'ERROR') {
            return this.translate.instant('invoicing.common.error');
        }
        if (status === 'VALIDEE') {
            return this.translate.instant('callInvoiceGeneration.validated');
        }
        if (status === 'NON_VALIDEE') {
            return this.translate.instant('callInvoiceGeneration.notValidated');
        }
        return status || '-';
    }

    protected getGenerationStatusInfo(generation: CallInvoiceGenerationView) {
        let date = null;
        if (generation.status === 'SEND') {
            date = generation.sentAt || generation.sendedAt;
        } else if (generation.status === 'PLANIFIEE') {
            date = generation.plannedSendDate;
        } else if (generation.status === 'VALIDEE') {
            date = generation.validatedAt;
        } else if (generation.status === 'ERROR') {
            return generation.errorMsg;
        }
        if (date) {
            return new Intl.DateTimeFormat(this.locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        }
        return '';
    }

    getGenerationStatusClass(status?: string | null): string {
        if (status === 'SEND') {
            return 'generation-status-badge generation-status-badge--sent';
        }
        if (status === 'PLANIFIEE') {
            return 'generation-status-badge generation-status-badge--scheduled';
        }
        if (status === 'ERROR') {
            return 'generation-status-badge generation-status-badge--error';
        }
        if (status === 'VALIDEE') {
            return 'generation-status-badge generation-status-badge--valid';
        }
        if (status === 'NON_VALIDEE') {
            return 'generation-status-badge generation-status-badge--pending';
        }
        return 'generation-status-badge';
    }

    canShowValidateButton(generation?: CallInvoiceGenerationView | null): boolean {
        return generation?.status === 'NON_VALIDEE';
    }

    canShowSendButton(generation?: CallInvoiceGenerationView | null): boolean {
        const status = generation?.status || '';
        return ['SEND', 'VALIDEE', 'ERROR', 'PLANIFIEE'].includes(status);
    }

    getGenerationStatusIcon(status?: string | null): string {
        if (status === 'PLANIFIEE') {
            return 'pi pi-clock';
        }
        return '';
    }

    validateGeneration(generation: CallInvoiceGenerationView): void {
        if (!this.callInvoice?.id || !generation?.id || this.generationActionLoading) {
            return;
        }

        this.generationActionLoading = true;
        this.engineSearchInvoiceService.validateGeneration(this.datastore, this.callInvoice.id, generation.id)
            .pipe(finalize(() => (this.generationActionLoading = false)))
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('callInvoiceGeneration.validation'),
                        detail: this.translate.instant('callInvoiceGeneration.validationDone')
                    });
                    this.loadCallInvoice(this.callInvoice.id);
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('callInvoiceGeneration.validationFailed'),
                        detail: err?.message || this.translate.instant('callInvoiceGeneration.validationUnavailable')
                    });
                }
            });
    }

    sendGenerationEmails(generation: CallInvoiceGenerationView): void {
        if (!this.callInvoice?.id || !generation?.id || this.generationActionLoading) {
            return;
        }

        this.generationActionLoading = true;
        this.engineSearchInvoiceService.sendGenerationEmails(this.datastore, this.callInvoice.id, generation.id)
            .pipe(finalize(() => (this.generationActionLoading = false)))
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('callInvoiceGeneration.sending'),
                        detail: this.translate.instant('callInvoiceGeneration.sendingDone')
                    });
                    this.loadCallInvoice(this.callInvoice.id);
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('callInvoiceGeneration.sendingFailed'),
                        detail: err?.message || this.translate.instant('callInvoiceGeneration.sendingUnavailable')
                    });
                }
            });
    }

    private buildSendSplitActions(generation: CallInvoiceGenerationView): MenuItem[] {
        return [{
            label: this.translate.instant('callInvoiceGeneration.schedule'),
            icon: 'pi pi-clock',
            command: () => this.openScheduleDialog(generation)
        }];
    }

    openScheduleDialog(generation: CallInvoiceGenerationView): void {
        if (!generation?.id || this.generationActionLoading) {
            return;
        }
        this.scheduleTargetGeneration = generation;
        this.scheduleSendDate = generation.plannedSendDate instanceof Date ? generation.plannedSendDate : null;
        this.scheduleDialogVisible = true;
    }

    closeScheduleDialog(): void {
        this.scheduleDialogVisible = false;
        this.scheduleSendDate = null;
        this.scheduleTargetGeneration = null;
    }

    confirmScheduleSend(): void {
        if (!this.callInvoice?.id || !this.scheduleTargetGeneration?.id || !this.scheduleSendDate) {
            return;
        }
        const body: CallInvoiceScheduleSendRequest = {
            sendDate: this.formatDateForApi(this.scheduleSendDate)
        };

        this.engineSearchInvoiceService
            .scheduleSendGeneration(body, this.datastore, this.callInvoice.id, this.scheduleTargetGeneration.id)
            .subscribe(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('callInvoiceGeneration.scheduling'),
                    detail: this.translate.instant('callInvoiceGeneration.schedulingDone')
                });
                const callInvoiceId = this.callInvoice?.id;
                this.closeScheduleDialog();
                if (callInvoiceId) {
                    this.loadCallInvoice(callInvoiceId);
                }
            });
    }

    private formatDateForApi(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    deleteGeneration(generation: CallInvoiceGenerationView): void {
        if (!this.callInvoice?.id || !generation?.id || this.generationActionLoading) {
            return;
        }
        const confirmed = window.confirm(this.translate.instant('callInvoiceGeneration.deleteConfirm'));
        if (!confirmed) {
            return;
        }

        this.generationActionLoading = true;
        this.engineSearchInvoiceService.deleteGeneration(this.datastore, this.callInvoice.id, generation.id)
            .pipe(finalize(() => (this.generationActionLoading = false)))
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('callInvoiceGeneration.deletion'),
                        detail: this.translate.instant('callInvoiceGeneration.deleted')
                    });
                    this.loadCallInvoice(this.callInvoice.id);
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('callInvoiceGeneration.deletionFailed'),
                        detail: err?.message || this.translate.instant('callInvoiceGeneration.deletionUnavailable')
                    });
                }
            });
    }

    private initList(): void {
        this.amountModes = [
            {label: this.translate.instant('callInvoiceConfiguration.totalSales'), value: 'VENTES'},
            {label: this.translate.instant('callInvoiceConfiguration.totalProfits'), value: 'PROFITS'},
        ];
        this.billingFrequencies = [
            {label: this.translate.instant('invoicing.common.monthly'), value: 'M'},
            {label: this.translate.instant('invoicing.common.quarterly'), value: 'T'}
        ];
        this.typesTransaction = [{
            label: '...',
            value: '',
        }, {
            label: this.translate.instant('invoicing.common.sales'), value: 'command', disabled: false
        }, {
            label: this.translate.instant('invoicing.common.leads'), value: 'lead', disabled: false
        }];
        this.typesDate = [{
            label: '...',
            value: '',
        }, {
            label: this.translate.instant('callInvoiceConfiguration.bookingDate'), value: 'RESERVATION_DATE'
        }, {
            label: this.translate.instant('callInvoiceConfiguration.travelDate'), value: 'TRAVEL_DATE'
        }];
    }

    private billingEmailDisplay(mail: {email?: string | null; recipients?: Array<{email?: string | null}>} | null | undefined): string | null {
        const addresses = new Map<string, string>();
        for (const recipient of mail?.recipients || []) {
            const address = recipient?.email?.trim();
            if (address && !addresses.has(address.toLowerCase())) { addresses.set(address.toLowerCase(), address); }
        }
        return addresses.size ? Array.from(addresses.values()).join(', ') : mail?.email || null;
    }

    openEMail(data: { partner: string; value: GenerateData }) {
        const partner = data.partner;
        const idCallinvoice = this.callInvoice.id;
        this.resetPreviews();
        this.previewVisible = true;
        this.activePreviewTab = 'email';

        this.currentData = {
            partner,
            value: data.value,
            email: this.billingEmailDisplay(data.value?.invoiceMail)
        };
        this.engineSearchInvoiceService.previewEmail(this.datastore, idCallinvoice, partner).subscribe(res => {
            res.htmlContent = this.extractHtmlFromPreview(res.htmlContent);
            this.templateEmail = res;
        });
    }

    openPDf(data: { partner: string; value: GenerateData }) {
        const partner = data.partner;
        const idCallinvoice = this.callInvoice.id;

        this.resetPreviews();
        this.previewVisible = true;
        this.activePreviewTab = 'pdf';
        this.engineSearchInvoiceService.downloadPdf(this.datastore, idCallinvoice, partner, 'response').subscribe(
            (response) => {
                this.showPdfPreview(response);
            });
    }

    openGenerationEmail(generation: CallInvoiceGenerationView, affiliation: CallInvoiceGenerationAffiliationView): void {
        this.openDocumentPreview(generation, affiliation, 'email');
    }

    openGenerationPdf(generation: CallInvoiceGenerationView, affiliation: CallInvoiceGenerationAffiliationView): void {
        this.openDocumentPreview(generation, affiliation, 'pdf');
    }

    private openDocumentPreview(generation: CallInvoiceGenerationView, affiliation: CallInvoiceGenerationAffiliationView,
                                tab: 'pdf' | 'excel' | 'email', file?: CallInvoiceGenerationFileView): void {
        if (!this.callInvoice?.id || !generation?.id || !affiliation?.affiliation) { return; }
        if (this.currentData?.generationId !== generation.id || this.currentData?.partner !== affiliation.affiliation) {
            this.resetPreviews();
        }
        this.previewGeneration = generation;
        this.previewAffiliation = affiliation;
        this.currentData = {partner: affiliation.affiliation, generationId: generation.id,
            email: this.billingEmailDisplay(affiliation.invoiceMail)};
        this.previewVisible = true;
        if (file) { this.activeExcelFile = file; this.excelPreviewBlob = undefined; }
        this.selectPreviewTab(tab);
    }

    selectPreviewTab(tab: 'pdf' | 'excel' | 'email'): void {
        this.previewCancel$.next();
        this.previewLoading = false;
        this.previewError = null;
        this.activePreviewTab = tab;
        const generation = this.previewGeneration;
        const affiliation = this.previewAffiliation;
        if (!generation?.id || !affiliation?.affiliation) { return; }
        if (tab === 'pdf' && this.pdfUrl || tab === 'email' && this.templateEmail || tab === 'excel' && this.excelPreviewBlob) { return; }
        this.previewLoading = true;
        const finish = <T>(request: Observable<T>): Observable<T> => request.pipe(
            takeUntil(this.previewCancel$), takeUntil(this.destroy$), finalize(() => this.previewLoading = false));
        const fail = (err: any) => this.previewError = err?.error?.detail || err?.message || this.translate.instant('invoicing.preview.unavailable');
        if (tab === 'pdf') {
            finish(this.engineSearchInvoiceService.downloadPdfByGeneration(this.datastore, this.callInvoice.id, generation.id, affiliation.affiliation, 'response'))
                .subscribe({next: response => this.showPdfPreview(response), error: fail});
        } else if (tab === 'email') {
            finish(this.invokePreviewEmailForGeneration(this.datastore, this.callInvoice.id, generation.id, affiliation.affiliation))
                .subscribe({next: result => this.templateEmail = {...result, htmlContent: this.extractHtmlFromPreview(result.htmlContent)}, error: fail});
        } else {
            this.activeExcelFile = this.activeExcelFile || affiliation.excelFiles?.[0];
            const file = this.activeExcelFile;
            if (!file?.id && !file?.fileName) { this.previewLoading = false; this.previewError = this.translate.instant('invoicing.preview.noExcel'); return; }
            finish(this.engineSearchInvoiceService.downloadExcel(this.datastore, this.callInvoice.id, generation.id, affiliation.affiliation,
                file.id || file.fileName, 'response')).subscribe({next: response => {
                    this.excelPreviewBlob = this.extractBlobFromResponse(response, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                    this.excelPreviewFilename = this.extractFilenameFromContentDisposition(this.getResponseHeader(response, 'content-disposition')) || file.fileName || 'export.xlsx';
                    if (!this.excelPreviewBlob) { this.previewError = this.translate.instant('invoicing.preview.excelEmpty'); }
                }, error: fail});
        }
    }

    selectPreviewExcel(file: CallInvoiceGenerationFileView): void {
        this.activeExcelFile = file;
        this.excelPreviewBlob = undefined;
        this.selectPreviewTab('excel');
    }

    previewTabKeydown(event: KeyboardEvent): void {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) { return; }
        const tabs = Array.from((event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)'));
        const current = tabs.indexOf(event.target as HTMLButtonElement);
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
            : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        event.preventDefault();
        tabs[index]?.click();
        tabs[index]?.focus();
    }

    get previewReference(): string { return String(this.previewAffiliation?.invoiceMail?.reference || ''); }

    get previewFiles(): CallInvoiceGenerationFileView[] {
        return [this.previewAffiliation?.pdfFile, ...(this.previewAffiliation?.excelFiles || [])].filter(file => !!file);
    }

    downloadPreviewZip(): void {
        if (!this.previewGeneration?.id || !this.currentData?.partner || this.downloadingZip) { return; }
        this.downloadingZip = true;
        this.engineSearchInvoiceService.downloadGenerationZip(this.datastore, this.callInvoice.id, this.previewGeneration.id, this.currentData.partner)
            .pipe(finalize(() => this.downloadingZip = false), takeUntil(this.destroy$)).subscribe({
                next: response => this.downloadBlobResponse(response, 'appel-a-facturation.zip'),
                error: err => this.messageService.add({severity: 'error', summary: this.translate.instant('invoicing.preview.downloadFailed'),
                    detail: err?.error?.detail || this.translate.instant('invoicing.preview.zipFailed')})
            });
    }

    downloadGenerationExcel(
        generation: CallInvoiceGenerationView,
        affiliation: CallInvoiceGenerationAffiliationView,
        excelFile: CallInvoiceGenerationFileView
    ): void {
        if (!this.callInvoice?.id || !generation?.id || !affiliation?.affiliation) {
            return;
        }
        if (!excelFile?.id && !excelFile?.fileName) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('invoicing.preview.fileUnavailable'),
                detail: this.translate.instant('invoicing.preview.excelMissing')
            });
            return;
        }

        this.engineSearchInvoiceService.downloadExcel(
            this.datastore,
            this.callInvoice.id,
            generation.id,
            affiliation.affiliation,
            excelFile.id || excelFile.fileName || '',
            'response'
        ).subscribe({
            next: (response) => {
                this.downloadBlobResponse(response, excelFile.fileName || 'export.xlsx');
            },
            error: (err) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('invoicing.preview.downloadFailed'),
                    detail: err?.message || this.translate.instant('invoicing.preview.excelUnavailable')
                });
            }
        });
    }

    previewGenerationExcel(generation: CallInvoiceGenerationView, affiliation: CallInvoiceGenerationAffiliationView,
                           excelFile: CallInvoiceGenerationFileView): void {
        this.openDocumentPreview(generation, affiliation, 'excel', excelFile);
    }

    getExcelButtonLabel(file: CallInvoiceGenerationFileView, index: number): string {
        if (file?.productType) {
            const labels = {flight: this.translate.instant('invoicing.common.flights'), hotel: this.translate.instant('invoicing.common.hotels'), car: this.translate.instant('invoicing.common.cars'), parking: this.translate.instant('invoicing.common.parkings')};
            return `Excel ${labels[file.productType.toLowerCase()] || file.productType}`;
        }
        if (file?.fileName) {
            return file.fileName;
        }
        return `Excel ${index + 1}`;
    }

    private buildExcelSplitActions(
        generation: CallInvoiceGenerationView,
        affiliation: CallInvoiceGenerationAffiliationView,
        excelFile: CallInvoiceGenerationFileView
    ): MenuItem[] {
        return [
            {
                label: this.translate.instant('invoicing.preview.download'),
                icon: 'pi pi-download',
                command: () => this.downloadGenerationExcel(generation, affiliation, excelFile)
            }
        ];
    }

    private invokePreviewEmailForGeneration(
        datastore: string,
        callInvoiceId: string,
        generationId: string,
        partner: string
    ): Observable<GetSmtpTemplateOverviewDTO> {
        const api = this.engineSearchInvoiceService as unknown as {
            previewEmailByGeneration?: (
                d: string, callId: string, genId: string, p: string
            ) => Observable<GetSmtpTemplateOverviewDTO>;
            previewGenerationEmail?: (
                d: string, callId: string, genId: string, p: string
            ) => Observable<GetSmtpTemplateOverviewDTO>;
            previewGenerationEmailUsingGET?: (
                d: string, callId: string, genId: string, p: string
            ) => Observable<GetSmtpTemplateOverviewDTO>;
            previewEmailByGenerationUsingGET?: (
                d: string, callId: string, genId: string, p: string
            ) => Observable<GetSmtpTemplateOverviewDTO>;
        };

        if (typeof api.previewEmailByGeneration === 'function') {
            return api.previewEmailByGeneration(datastore, callInvoiceId, generationId, partner);
        }
        if (typeof api.previewGenerationEmail === 'function') {
            return api.previewGenerationEmail(datastore, callInvoiceId, generationId, partner);
        }
        if (typeof api.previewGenerationEmailUsingGET === 'function') {
            return api.previewGenerationEmailUsingGET(datastore, callInvoiceId, generationId, partner);
        }
        if (typeof api.previewEmailByGenerationUsingGET === 'function') {
            return api.previewEmailByGenerationUsingGET(datastore, callInvoiceId, generationId, partner);
        }

        return this.engineSearchInvoiceService.previewEmail(datastore, callInvoiceId, partner);
    }


    private extractHtmlFromPreview(rawHtml: string): string {
        if (!rawHtml) {
            return rawHtml;
        }
        try {
            const parsed = JSON.parse(rawHtml);
            if (parsed?.html) {
                return parsed.html;
            }
        } catch (_error) {
            // htmlContent déjà rendu en HTML, rien à faire.
        }
        return rawHtml;
    }

    private showPdfPreview(responseOrBody: any): void {
        const pdfBlob = this.extractBlobFromResponse(responseOrBody, 'application/pdf');
        if (!pdfBlob) {
            this.previewError = this.translate.instant('invoicing.preview.pdfEmpty');
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('invoicing.preview.pdfUnavailable'),
                detail: this.translate.instant('invoicing.preview.pdfEmpty')
            });
            return;
        }

        this.objectUrl = URL.createObjectURL(pdfBlob);
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    }

    private downloadBlobResponse(responseOrBody: any, fallbackFilename: string): void {
        const contentDisposition = this.getResponseHeader(responseOrBody, 'content-disposition');
        const filename = this.extractFilenameFromContentDisposition(contentDisposition) || fallbackFilename;
        const blob = this.extractBlobFromResponse(responseOrBody, 'application/octet-stream');
        if (!blob) {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('invoicing.preview.downloadFailed'),
                detail: this.translate.instant('invoicing.preview.fileEmpty')
            });
            return;
        }
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }

    private extractFilenameFromContentDisposition(contentDisposition: string): string {
        if (!contentDisposition) {
            return '';
        }
        const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
        if (utf8Match?.[1]) {
            try {
                return decodeURIComponent(utf8Match[1].trim());
            } catch (_error) {
                return utf8Match[1].trim();
            }
        }
        const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(contentDisposition);
        return filenameMatch ? filenameMatch[1].replace(/['"]/g, '').trim() : '';
    }

    private getResponseHeader(responseOrBody: any, headerName: string): string {
        if (!responseOrBody?.headers?.get) {
            return '';
        }
        return responseOrBody.headers.get(headerName)
            || responseOrBody.headers.get(headerName.toLowerCase())
            || responseOrBody.headers.get(headerName.toUpperCase())
            || '';
    }

    private extractBlobFromResponse(responseOrBody: any, fallbackContentType: string): Blob | null {
        const body = responseOrBody?.body ?? responseOrBody;
        if (!body) {
            return null;
        }

        const contentType = this.getResponseHeader(responseOrBody, 'content-type') || fallbackContentType;
        if (body instanceof Blob) {
            return body.size ? (body.type ? body : new Blob([body], {type: contentType})) : null;
        }
        if (body instanceof ArrayBuffer) {
            return new Blob([body], {type: contentType});
        }
        if (ArrayBuffer.isView(body)) {
            return new Blob([body.buffer], {type: contentType});
        }
        return new Blob([body], {type: contentType});
    }

    protected resetPreviews(): void {
        this.previewCancel$.next();
        this.previewVisible = false;
        this.previewGeneration = null;
        this.previewAffiliation = null;
        this.activeExcelFile = null;
        this.previewError = null;
        this.previewLoading = false;
        this.currentData = null;
        this.templateEmail = null;
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = undefined;
        }
        this.pdfUrl = undefined;
        this.excelPreviewBlob = undefined;
        this.excelPreviewFilename = undefined;
    }

    sendTestMail() {
        this.testEmailControl.setValue(this.email.trim());
        this.testEmailControl.markAsTouched();
        if (!this.currentData?.partner || this.testEmailControl.invalid || this.testEmailLoading || this.templateEmail?.errorMsg) { return; }
        const idCallinvoice = this.callInvoice.id;
        const partner = this.currentData.partner;
        const request = this.currentData.generationId
            ? this.engineSearchInvoiceService.sendTestEmailByGeneration(
                this.datastore, idCallinvoice, this.currentData.generationId, partner, this.email)
            : this.engineSearchInvoiceService.sendTestEmail1(this.datastore, idCallinvoice, partner, this.email);
        this.testEmailLoading = true;
        request.pipe(finalize(() => this.testEmailLoading = false), takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('invoicing.preview.testMail'),
                    detail: this.translate.instant('invoicing.preview.testSent')
                });
            },
            error: (err) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('callInvoiceGeneration.sendingFailed'),
                    detail: err?.error?.detail || err?.error?.message || err?.message || this.translate.instant('invoicing.preview.testFailed')
                });
            }
        });
    }
}
